/* eslint-disable */

import DOMPurify from 'dompurify';
import { getBasicAuthForSportradar } from '../../../../themes/arsenal/assets/js/shared/shared';

/**
 * Scoreboard
 *
 * Used to display the scoreboard across the top of the site.
 */
const sportradarScoreboard = {

	/**
	 * Initialize the scoreboard, checking for required elements.
	 */
	init() {
		window.nesn_debug && console.time('scoreboard-init');
		// Get the container, return if not found
		this.scoreboardLocationDesktop = document.querySelector( '.js-scoreboard' );
		this.scoreboardLocationDesktop && this.fetchData();
	},


	/**
	 * Fetch the data from the scoreboard API endpoint
	 */
	fetchData() {
		window.nesn_debug && console.time('scoreboard-fetch');
		const headers = new Headers();
		const auth    = getBasicAuthForSportradar();
		headers.append( 'Authorization', `Basic ${auth}` );
		fetch(
			// Note that by excluding the leagues, we get the defaults, which are defined in the WP admin
			`${window.arsenalVars.restEndpoint}sportradar/v2/scoreboard/?teams=${window.arsenalVars.scoreboardTeams}`,
			{
				method: 'GET',
				headers
			} )
		.then( response => {
			window.nesn_debug && console.timeEnd('scoreboard-fetch');
			// Check if the response is successful
			if ( !response.ok ) {
				throw new Error( 'fetchData: Network response was not ok' );
			}
			return response.json();
		} ).then( fetchedData => {
			window.nesn_debug && console.time('scoreboard-render');
			if ( !fetchedData ) {
				throw new Error( 'fetchData: No data found' );
			}
			this.createScoreboard( fetchedData.byDate, this.scoreboardLocationDesktop );
			this.doScoreboardSlider();
			window.nesn_debug && console.timeEnd('scoreboard-render');
			window.nesn_debug && console.timeEnd('scoreboard-init');
		} )

	},


	/**
	 * Create and display the scoreboard
	 *
	 * @param scoreboardData      Fetched JSON data
	 * @param scoreboardLocation  The location to update
	 */
	createScoreboard( scoreboardData, scoreboardLocation ) {
		// For each date...
		Object.keys( scoreboardData ).forEach( date => {

			// Create the date container
			let byDate = document.createElement( 'div' );
			byDate.classList.add( 'by-date' );
			byDate.setAttribute( 'data-date', date );
			byDate.setAttribute( 'aria-hidden', false );
			byDate.setAttribute( 'tabindex', 0 );
			let dateHtml = document.createElement( 'div' );
			dateHtml.classList.add( 'date' );
			dateHtml.innerHTML = this.getDateHtml( date );
			byDate.appendChild( dateHtml );

				// For each league...
			Object.keys( scoreboardData[date] ).forEach( league => {

				// Create the league container
				let byLeague = document.createElement( 'div' );
				byLeague.classList.add( 'by-league' );
				byLeague.setAttribute( 'data-league', league );
				byDate.appendChild( byLeague );

				Object.keys( scoreboardData[ date ][ league ] ).forEach( match => {

					this.createScoreBox(
						{
							league: league,
							match : scoreboardData[ date ][ league ][match]
						},
						byLeague
					);
				})
			})
			const scoreboardLoadingElements = scoreboardLocation.querySelectorAll('.scoreboard-loading');
			scoreboardLoadingElements.forEach(element => element.remove());

			scoreboardLocation.appendChild( byDate );
		})

		// Create the skip link element on desktop site
		if (window.innerWidth > 768 && scoreboardLocation) {
			const skipLink = document.createElement("a");
			skipLink.href = "#v2-nav-main-left"; // Set the target for the skip link
			skipLink.className = "skip-link screen-reader-text";
			skipLink.setAttribute("aria-hidden", "false");
			skipLink.textContent = "Skip scoreboard";
			scoreboardLocation.insertBefore(skipLink, scoreboardLocation.firstChild);
		}

	},

	/**
	 * Gets the relative date (meaning with "Today" and "Tomorrow" as appropriate)
	 *
	 * @param date        Date in yyyy-mm-dd format
	 * @returns {string}  HTML-ified relative date
	 */
	getDateHtml( date ) {
		// time offset is required to ensure proper time zone handling
		let suppliedDate = new Date( date + 'T00:00:00' );

		// We only care about the date, not the time
		suppliedDate.setHours( 0, 0, 0, 0 );
		let dayOfWeek = suppliedDate.toLocaleDateString( 'en-US', {
			weekday: 'long',
		} );

		// Check for "Today"
		let checkDate = new Date();
		checkDate.setHours( 0, 0, 0, 0 );
		if ( suppliedDate.toDateString() === checkDate.toDateString() ) {
			dayOfWeek = 'Today';
		}

		// Check for "Tomorrow"
		checkDate.setDate( checkDate.getDate() + 1 );
		if ( suppliedDate.toDateString() === checkDate.toDateString() ) {
			dayOfWeek = 'Tomorrow';
		}

		// Check for "Yesterday"
		checkDate.setDate( checkDate.getDate() -2 );
		if ( suppliedDate.toDateString() === checkDate.toDateString() ) {
			dayOfWeek = 'Yesterday';
		}


		let dayOfMonth = suppliedDate.toLocaleDateString( 'en-US', { day: 'numeric' } );
		let month = suppliedDate.toLocaleDateString( 'en-US', { month: 'short' } );
		let longMonth = suppliedDate.toLocaleDateString( 'en-US', { month: 'long' } );

		return `<span class="day-of-week">${dayOfWeek}</span><span class="month" aria-label="${longMonth}">${month}</span><span class="day-of-month">${dayOfMonth}</span>`;
	},


	/**
	 * Create and display each match's info in the standard markup.
	 *
	 * @param matchData  Array of JSON for the match, with the away and home logos added
	 * @param byLeague   The league container
	 */
	createScoreBox( matchData, byLeague ) {
		// DOMPurify Config
		const config = {
			USE_PROFILES       : { svg: true, svgFilters: true },
			ADD_TAGS           : [ 'use', 'div', 'span' ],
			ADD_ATTR           : ['target'],
			RETURN_DOM_FRAGMENT: true,
			RETURN_DOM_IMPORT  : true,
		};
		let wrapperOpen = '';
		let wrapperClose = '';
		const isNESN = 1 == window.arsenalVars.isNESN;
		// In this case, type coersion is desired
		if (1 == window.arsenalVars.isNESN ) {
			wrapperOpen = `<a href="${matchData.match.overlayLink.link.toString()}" target="${matchData.match.overlayLink.target.toString()}" data-analytics-datalayer-category-type="${matchData.league.toString()}" class="box-score--overlay-trigger">`;
			wrapperClose = `</a>`;
		}

		let awayLogoSvg =  this.addAriaLabelToSvg(matchData.match.away.logoSvg, matchData.match.away.name);
		let homeLogoSvg =  this.addAriaLabelToSvg(matchData.match.home.logoSvg, matchData.match.home.name);

		const roughHtml = wrapperOpen + `
			
				<div class="box-score ${this.getGameStatusClass( matchData )}">
						<div class="box-score--overlay">
							<span aria-hidden="${!isNESN}" class="button button--small">
								${matchData.match.overlayLink.label.toString()}
							</span>
						</div>
					<div class="box-score--header">
						<span class="box-score--league">
							${matchData.league.toString()}
						</span>
						${this.checkGameStatus( matchData )}
					</div>
					<div class="box-score--inner">
						<div class="box-score--team ${this.checkWinner( matchData.match.away.score, matchData.match.home.score )}" aria-label="${matchData.match.away.name}">
							${awayLogoSvg}
							${matchData.match.away.alias.toString()}
						</div>
						<div class="box-score--score ${this.checkWinner( matchData.match.away.score, matchData.match.home.score )}" ${this.checkScoresForAria(matchData.match.away.score)}>
							<span>${matchData.match.away.score.toString()}</span>
							<svg viewBox="0 0 7 12" class="nesn-icon" aria-hidden="true">
								<use xlink:href="${window.arsenalVars.imagesPlugin}/dist/svg/arrow.svg"></use>
							</svg>
						</div>
						<div class="box-score--team ${this.checkWinner( matchData.match.home.score, matchData.match.away.score )}" aria-label="${matchData.match.home.name}">
							${homeLogoSvg}
							${matchData.match.home.alias.toString()}
						</div>
						<div class="box-score--score ${this.checkWinner( matchData.match.home.score, matchData.match.away.score )}" ${this.checkScoresForAria(matchData.match.home.score)}>
							<span>${matchData.match.home.score.toString()}</span>
							<svg viewBox="0 0 7 12" class="nesn-icon" aria-hidden="true">
								<use xlink:href="${window.arsenalVars.imagesPlugin}/dist/svg/arrow.svg"></use>
							</svg>
						</div>
					</div>
				</div>` + wrapperClose;

		const cleanHtml = DOMPurify.sanitize( roughHtml, config );
		byLeague.appendChild( cleanHtml );
	},


	/**
	 * Determine the winner for a game and set a class appropriately
	 *
	 * @todo: don't do this if game is not closed
	 *
	 * @param teamScore
	 * @param otherTeamScore
	 * @returns {string}
	 */
	checkWinner( teamScore, otherTeamScore ) {
		if ( parseInt( teamScore, 10 ) > parseInt( otherTeamScore, 10 ) ) {
			return 'box-score--win';
		} else {
			return '';
		}
	},
	/**
	 * Add aria-label to the logoSvg
	 * @param {*} svg
	 * @param {*} name
	 * @returns {string}
	 */
	addAriaLabelToSvg (svg, name) {
		return svg.replace(
			'<svg',
			`<svg aria-label="${name}" tabindex=-1 aria-hidden="true"`
		);
	},

	/**
	 * Determine if the team should have a aria-label for scores
	 * @param {*} teamScore
	 * @returns {string}
	 */
	checkScoresForAria ( teamScore ) {
		if( parseInt( teamScore ) > 0 ) {
			return `aria-label="scores ${teamScore}"`
		} else {
			return null
		}
	},

	/**
	 * Check the game status. Accounts for all possible SR game status'.
	 *
	 * @param matchData     One of the valid SR game status'
	 * @returns {string} The game start time in UTC
	 */
	checkGameStatus( matchData ) {
		let status = matchData.match.status.toString();
		let startTime = matchData.match.scheduledStart.toString();

		if ( 'closed' === status
			|| 'complete' === status ) {
			//return '<span class="box-score--status box-score--status__final">Final</span>';
			return '<span class="box-score--status">Final</span>';
		} else if ( 'scheduled' === status
			|| 'created' === status
			|| 'not_started' === status //PWHL
			|| 'flex-schedule' === status ) {
			// This is UTC
			let suppliedDate = new Date( startTime );
			//return `<span class="box-score--status box-score--status__scheduled">${suppliedDate.toLocaleTimeString( 'en-US', { timeStyle: "short" })} ET</span>`;
			return `<span class="box-score--status">${suppliedDate.toLocaleTimeString( 'en-US', { timeStyle: "short", timeZone: "America/New_York" } )} ET</span>`;
		} else if ( 'inprogress' === status ) {
			//return `<span class="box-score--status box-score--status__inprogress">${matchData.match.niceClock.toString()}</span>`;
			return `<span class="box-score--period">${matchData.match.niceClock.toString()}</span><span class="box-score--status">&#8226; LIVE</span>`;
		} else if ( 'halftime' === status ) {
			//return `<span class="box-score--status box-score--status__halftime">Halftime</span>`;
			return `<span class="box-score--period">Halftime</span><span class="box-score--status">&#8226; LIVE</span>`;
		} else {
			// cancelled postponed delayed time-tbd
			//return '<span class="box-score--status  box-score--status__none">n/a</span>';
			return '<span class="box-score--status">TBD</span>';
		}
	},


	/**
	 * Check the game status. Accounts for all possible SR game status'.
	 *
	 * @param matchData     One of the valid SR game status'
	 * @returns {string} The game start time in UTC
	 */
	getGameStatusClass( matchData ) {
		let status    = matchData.match.status.toString();
		let startTime = matchData.match.scheduledStart.toString();

		if ( 'closed' === status
			|| 'complete' === status ) {
			return 'box-score--status__final';
		} else if ( 'scheduled' === status
			|| 'created' === status
			|| 'flex-schedule' === status ) {
			// This is UTC
			let suppliedDate = new Date( startTime );
			return `box-score--status__scheduled`;
		} else if ( 'inprogress' === status ) {
			return `box-score--status__inprogress`;
		} else if ( 'halftime' === status ) {
			return `box-score--status__halftime`;
		} else {
			// cancelled postponed delayed time-tbd
			return 'box-score--status__none';
		}
	},

	/**
	 * Add the slick slider bits to the now completed scoreboard. Slick is loading via npm, and
	 * the default styles are included in frontend-v2/styles-v2.css. Ideally this component
	 * would be its own thing/module/whatever but I don't know how to do that yet.
	 *
	 * Options are base don those found in WatchListCarousel
	 */
	doScoreboardSlider() {
		const $sliderScoreboard = jQuery('.js-scoreboard');

		// Check if the slider and its children exist
		if ( ! $sliderScoreboard.length || ! $sliderScoreboard.children().length ) {
			return;
		}

		// Ensure aria-hidden is set to false for all slick items
		$sliderScoreboard.on('init', function(event, slick) {
			jQuery(slick.$slides).attr('aria-hidden', false);
		});

		// Check if slick is already initialized
		if ( ! $sliderScoreboard.hasClass( 'slick-initialized' ) ) {
			$sliderScoreboard.slick( {
				accessibility: false,
				arrows: true,
				centerMode: false,
				slidesToScroll: 1,
				speed: 500,
				mobileFirst: false,
				swipe: true,
				fade: false,
				autoplay: false,
				adaptiveHeight: false,
				variableWidth: true,
				slidesToShow: 1,
				infinite: false,
				draggable: true,
				prevArrow: "<div class='slick-prev slick-arrow'><span><svg aria-label='Previous slide' xmlns='http://www.w3.org/2000/svg' width='10' height='16' fill='none' viewBox='0 0 10 16'><path fill='#d5fa2c' d='M9.88 14.12 3.773 8 9.88 1.88 8 0 0 8l8 8 1.88-1.88Z' /></svg></span></div>",
				nextArrow: "<div class='slick-next slick-arrow'><span><svg aria-label='Next slide' xmlns='http://www.w3.org/2000/svg' width='10' height='16' fill='none' viewbox='0 0 10 16'><path fill='#d5fa2c' d= 'M0 14.12 6.107 8 0 1.88 1.88 0l8 8-8 8L0 14.12Z' /></svg>",

			} );
		}

		// Reapply aria-hidden after slide change
		$sliderScoreboard.on('afterChange', function(event, slick, currentSlide) {
			slick.$slides.each(function(index, slide) {
				jQuery(slick.$slides).attr('aria-hidden', false);
			});
    });
	},

}

export default sportradarScoreboard;
